<script lang="ts">
    import AppLayout from '@/layouts/AppLayout.svelte';
    import { type BreadcrumbItem } from '@/types';
    import * as Card from '@/components/ui/card';
    import * as Select from '@/components/ui/select';
    import * as DropdownMenu from '@/components/ui/dropdown-menu';
    import { Button } from '@/components/ui/button';
    import { Badge } from '@/components/ui/badge';
    import { router } from '@inertiajs/svelte';
    import TaskForm from '@/components/activity/TaskForm.svelte';
    import MessageForm from '@/components/activity/MessageForm.svelte';
    import {
        CheckCircle2,
        Circle,
        Clock,
        MessageSquare,
        ClipboardList,
        Plus,
        Mail,
        MailOpen,
        ExternalLink
    } from 'lucide-svelte';

    type User = {
        id: string;
        name: string;
    };

    type Activity = {
        id: string;
        type: 'task' | 'message';
        title: string;
        description?: string;
        body?: string;
        priority: 'low' | 'medium' | 'high';
        status?: 'pending' | 'in_progress' | 'completed';
        is_read?: boolean;
        due_date?: string;
        created_at: string;
        creator: User;
        assignee?: User;
        related_record_url?: string | null;
        related_record_label?: string | null;
        related_record_type_name?: string | null;
    };

    type Props = {
        activities: {
            data: Activity[];
            total: number;
            per_page: number;
            current_page: number;
            last_page: number;
        };
        users: User[];
        filters: {
            type?: string;
            priority?: string;
            assigned_to?: string;
            my_items?: boolean;
            status?: string;
            is_read?: boolean;
            show_completed?: string;
            show_read?: string;
        };
        stats: {
            pending_tasks: number;
            in_progress_tasks: number;
            overdue_tasks: number;
            unread_messages: number;
        };
        taskBlocks: any[];
        messageBlocks: any[];
    };

    let { activities, users, filters, stats, taskBlocks, messageBlocks }: Props = $props();

    const breadcrumbs: BreadcrumbItem[] = [
        { title: 'Activity', href: '/activity' },
    ];

    function getPriorityColor(priority: string) {
        switch (priority) {
            case 'high': return 'destructive';
            case 'medium': return 'default';
            case 'low': return 'secondary';
            default: return 'default';
        }
    }

    function getStatusIcon(status?: string) {
        switch (status) {
            case 'completed': return CheckCircle2;
            case 'in_progress': return Clock;
            default: return Circle;
        }
    }

    function isActivityComplete(activity: Activity): boolean {
        if (activity.type === 'task') {
            return activity.status === 'completed';
        }
        return activity.is_read === true;
    }

    function formatDate(dateString: string) {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    function applyFilter(key: string, value: any) {
        router.get('/activity', {
            ...filters,
            [key]: value,
        }, {
            preserveState: true,
            preserveScroll: true,
        });
    }

    function viewItem(activity: Activity) {
        if (activity.type === 'task') {
            router.visit(`/tasks/${activity.id}`);
        } else {
            router.visit(`/messages/${activity.id}`);
        }
    }

    let showTaskForm = $state(false);
    let showMessageForm = $state(false);
</script>

<svelte:head>
    <title>Activity</title>
</svelte:head>

<AppLayout {breadcrumbs}>
    <div class="space-y-4 p-4">
        <!-- Stats Cards -->
        <div class="grid gap-4 md:grid-cols-4">
            <Card.Root>
                <Card.Header class="flex flex-row items-center justify-between pb-2">
                    <Card.Title class="text-sm font-medium">Pending Tasks</Card.Title>
                    <Circle class="h-4 w-4 text-muted-foreground" />
                </Card.Header>
                <Card.Content>
                    <div class="text-2xl font-bold">{stats.pending_tasks}</div>
                </Card.Content>
            </Card.Root>

            <Card.Root>
                <Card.Header class="flex flex-row items-center justify-between pb-2">
                    <Card.Title class="text-sm font-medium">In Progress</Card.Title>
                    <Clock class="h-4 w-4 text-muted-foreground" />
                </Card.Header>
                <Card.Content>
                    <div class="text-2xl font-bold">{stats.in_progress_tasks}</div>
                </Card.Content>
            </Card.Root>

            <Card.Root>
                <Card.Header class="flex flex-row items-center justify-between pb-2">
                    <Card.Title class="text-sm font-medium">Overdue</Card.Title>
                    <Clock class="h-4 w-4 text-destructive" />
                </Card.Header>
                <Card.Content>
                    <div class="text-2xl font-bold text-destructive">{stats.overdue_tasks}</div>
                </Card.Content>
            </Card.Root>

            <Card.Root>
                <Card.Header class="flex flex-row items-center justify-between pb-2">
                    <Card.Title class="text-sm font-medium">Unread Messages</Card.Title>
                    <MessageSquare class="h-4 w-4 text-muted-foreground" />
                </Card.Header>
                <Card.Content>
                    <div class="text-2xl font-bold">{stats.unread_messages}</div>
                </Card.Content>
            </Card.Root>
        </div>

        <!-- Main Content -->
        <Card.Root>
            <Card.Header>
                <div class="flex items-center justify-between">
                    <Card.Title>Tasks & Messages</Card.Title>
                    <div class="flex gap-2">
                        <DropdownMenu.Root>
                            <DropdownMenu.Trigger>
                                {#snippet child({ props })}
                                    <Button {...props} variant="outline" size="sm">
                                        <Plus class="h-4 w-4 mr-2" />
                                        New
                                    </Button>
                                {/snippet}
                            </DropdownMenu.Trigger>
                            <DropdownMenu.Content>
                                <DropdownMenu.Item onclick={() => showTaskForm = true}>
                                    <ClipboardList class="h-4 w-4 mr-2" />
                                    New Task
                                </DropdownMenu.Item>
                                <DropdownMenu.Item onclick={() => showMessageForm = true}>
                                    <MessageSquare class="h-4 w-4 mr-2" />
                                    New Message
                                </DropdownMenu.Item>
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    </div>
                </div>

                <!-- Filters -->
                <div class="flex flex-wrap gap-2 mt-4">
                    <Select.Root
                        selected={{ value: filters.type || 'all', label: filters.type || 'All' }}
                        onSelectedChange={(v) => v && applyFilter('type', v.value)}
                    >
                        <Select.Trigger class="w-[140px]">
                            <Select.Value placeholder="Type" />
                        </Select.Trigger>
                        <Select.Content>
                            <Select.Item value="all">All</Select.Item>
                            <Select.Item value="task">Tasks</Select.Item>
                            <Select.Item value="message">Messages</Select.Item>
                        </Select.Content>
                    </Select.Root>

                    <Select.Root
                        selected={{ value: filters.priority || 'all', label: filters.priority || 'All Priorities' }}
                        onSelectedChange={(v) => v && applyFilter('priority', v.value)}
                    >
                        <Select.Trigger class="w-[160px]">
                            <Select.Value placeholder="Priority" />
                        </Select.Trigger>
                        <Select.Content>
                            <Select.Item value="all">All Priorities</Select.Item>
                            <Select.Item value="high">High</Select.Item>
                            <Select.Item value="medium">Medium</Select.Item>
                            <Select.Item value="low">Low</Select.Item>
                        </Select.Content>
                    </Select.Root>

                    <Button
                        variant={filters.my_items ? 'default' : 'outline'}
                        size="sm"
                        onclick={() => applyFilter('my_items', !filters.my_items)}
                    >
                        My Items
                    </Button>

                    <Button
                        variant={filters.show_completed === 'false' ? 'default' : 'outline'}
                        size="sm"
                        onclick={() => applyFilter('show_completed', filters.show_completed === 'false' ? undefined : 'false')}
                    >
                        Hide Completed
                    </Button>

                    <Button
                        variant={filters.show_read === 'false' ? 'default' : 'outline'}
                        size="sm"
                        onclick={() => applyFilter('show_read', filters.show_read === 'false' ? undefined : 'false')}
                    >
                        Hide Read
                    </Button>
                </div>
            </Card.Header>

            <Card.Content>
                <div class="space-y-2">
                    {#each activities.data as activity}
                        <button
                            onclick={() => viewItem(activity)}
                            class="w-full text-left p-4 border rounded-lg hover:bg-accent transition-colors"
                        >
                            <div class="flex items-start gap-3">
                                <div class="mt-1">
                                    {#if activity.type === 'task'}
                                        {@const StatusIcon = getStatusIcon(activity.status)}
                                        <StatusIcon class="h-5 w-5" style="color: var({activity.status === 'completed' ? '--activity-complete' : '--error'});" />
                                    {:else}
                                        {#if activity.is_read}
                                            <MailOpen class="h-5 w-5" style="color:var(--activity-complete);" />
                                        {:else}
                                            <Mail class="h-5 w-5" style="color: hsl(var(--error));" />
                                        {/if}
                                    {/if}
                                </div>

                                <div class="flex-1 min-w-0">
                                    <div class="flex items-center gap-2 mb-1">
                                        <Badge variant="outline" class="text-xs">
                                            {activity.type === 'task' ? 'Task' : 'Message'}
                                        </Badge>
                                        <Badge variant={getPriorityColor(activity.priority)}>
                                            {activity.priority}
                                        </Badge>
                                        {#if activity.status}
                                            <Badge
                                                variant="secondary"
                                                class="text-xs"
                                                style="background-color: var({activity.status === 'completed' ? '--activity-complete'  : '--error'}); color: white;"
                                            >
                                                {activity.status.replace('_', ' ')}
                                            </Badge>
                                        {/if}
                                        {#if activity.type === 'message'}
                                            <Badge
                                                variant="secondary"
                                                class="text-xs"
                                                style="background-color: var({activity.is_read ? '--activity-complete'  : '--error'}); color: white;"
                                            >
                                                {activity.is_read ? 'read' : 'unread'}
                                            </Badge>
                                        {/if}
                                    </div>

                                    <h3 class="font-semibold text-foreground mb-1">{activity.title}</h3>

                                    {#if activity.description}
                                        <p class="text-sm text-muted-foreground line-clamp-1">
                                            {activity.description}
                                        </p>
                                    {/if}
                                    {#if activity.body}
                                        <p class="text-sm text-muted-foreground line-clamp-1">
                                            {activity.body}
                                        </p>
                                    {/if}

                                    {#if activity.related_record_url}
                                        <a
                                            href={activity.related_record_url}
                                            onclick={(e) => e.stopPropagation()}
                                            class="inline-flex items-center gap-1 text-sm text-primary hover:underline mt-1"
                                            target="_blank"
                                        >
                                            <ExternalLink class="h-3 w-3" />
                                            <span class="font-medium">{activity.related_record_type_name}:</span>
                                            <span>{activity.related_record_label}</span>
                                        </a>
                                    {/if}

                                    <div class="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                                        <span>Created by {activity.creator.name}</span>
                                        {#if activity.assignee}
                                            <span>• Assigned to {activity.assignee.name}</span>
                                        {/if}
                                        {#if activity.due_date}
                                            <span>• Due {formatDate(activity.due_date)}</span>
                                        {/if}
                                        <span>• {formatDate(activity.created_at)}</span>
                                    </div>
                                </div>
                            </div>
                        </button>
                    {:else}
                        <div class="text-center py-12 text-muted-foreground">
                            <ClipboardList class="h-12 w-12 mx-auto mb-4 opacity-50" />
                            <p>No activities found</p>
                        </div>
                    {/each}
                </div>

                <!-- Pagination -->
                {#if activities.last_page > 1}
                    <div class="flex justify-center gap-2 mt-6">
                        {#each Array(activities.last_page) as _, i}
                            <Button
                                variant={activities.current_page === i + 1 ? 'default' : 'outline'}
                                size="sm"
                                onclick={() => router.get('/activity', { ...filters, page: i + 1 })}
                            >
                                {i + 1}
                            </Button>
                        {/each}
                    </div>
                {/if}
            </Card.Content>
        </Card.Root>
    </div>

    <!-- Forms -->
    <TaskForm
        bind:open={showTaskForm}
        {users}
        blocks={taskBlocks}
        onClose={() => showTaskForm = false}
    />

    <MessageForm
        bind:open={showMessageForm}
        {users}
        blocks={messageBlocks}
        onClose={() => showMessageForm = false}
    />
</AppLayout>
